import { Graphics } from "pixi.js";
import type { Game } from "@engine/core/Game";
import type { DebugCategory } from "./DebugRegistry";

const HOTKEYS: Record<string, DebugCategory> = {
  F1: "stats",
  F2: "collision",
  F3: "chunks",
  F4: "net",
};

export type EnabledMap = Readonly<Record<DebugCategory, boolean>>;
type Listener = (enabled: EnabledMap) => void;

/**
 * Owns the Graphics on the "debug" render layer and the F1..F4 hotkey toggles.
 * Each frame it asks the registry for providers in active categories and lets
 * them draw on the shared Graphics.
 *
 *  F1 → stats   (HUD-side, no world-space drawing)
 *  F2 → collision
 *  F3 → chunks
 *  F4 → net
 */
export class DebugOverlay {
  readonly graphics: Graphics;
  private enabledState: { stats: boolean; collision: boolean; chunks: boolean; net: boolean } = {
    stats: false,
    collision: false,
    chunks: false,
    net: false,
  };
  private listeners = new Set<Listener>();
  private detachKey: (() => void) | undefined;

  constructor(private readonly game: Game) {
    this.graphics = new Graphics({ label: "debug:overlay" });
    this.game.layers.attach("debug", this.graphics);
  }

  attach(): void {
    if (this.detachKey) return;
    const onKey = (e: KeyboardEvent): void => {
      const cat = HOTKEYS[e.code];
      if (!cat) return;
      e.preventDefault();
      this.toggle(cat);
    };
    window.addEventListener("keydown", onKey);
    this.detachKey = () => window.removeEventListener("keydown", onKey);
  }

  detach(): void {
    this.detachKey?.();
    this.detachKey = undefined;
  }

  toggle(cat: DebugCategory): void {
    this.enabledState[cat] = !this.enabledState[cat];
    this.notify();
  }

  setEnabled(cat: DebugCategory, value: boolean): void {
    if (this.enabledState[cat] === value) return;
    this.enabledState[cat] = value;
    this.notify();
  }

  isEnabled(cat: DebugCategory): boolean {
    return this.enabledState[cat];
  }

  enabled(): EnabledMap {
    return { ...this.enabledState };
  }

  /** React-friendly subscription. Returns unsubscriber. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Redraws the world-space overlay. Call once per frame after the camera
   * has been applied so view bounds match.
   */
  render(): void {
    this.graphics.clear();
    if (!this.enabledState.collision && !this.enabledState.chunks && !this.enabledState.net) return;
    const view = this.game.camera.getViewBounds();
    if (this.enabledState.collision) this.drawCategory("collision", view);
    if (this.enabledState.chunks) this.drawCategory("chunks", view);
    if (this.enabledState.net) this.drawCategory("net", view);
  }

  /** Lines for the textual HUD panel — grouped by category. */
  getActiveLines(): string[] {
    const out: string[] = [];
    const cats: DebugCategory[] = ["stats", "collision", "chunks", "net"];
    for (const cat of cats) {
      if (!this.enabledState[cat]) continue;
      const providers = this.game.debug.list(cat);
      const header = `[${cat}]`;
      let pushedHeader = false;
      for (const p of providers) {
        if (!p.lines) continue;
        if (!pushedHeader) {
          out.push(header);
          pushedHeader = true;
        }
        const tag = p.name ? `· ${p.name}` : "";
        if (tag) out.push(`  ${tag}`);
        for (const line of p.lines()) out.push(`  ${line}`);
      }
    }
    return out;
  }

  destroy(): void {
    this.detach();
    if (this.graphics.parent) this.graphics.parent.removeChild(this.graphics);
    this.graphics.destroy({ children: true });
    this.listeners.clear();
  }

  private drawCategory(cat: DebugCategory, view: { minX: number; minY: number; maxX: number; maxY: number }): void {
    const providers = this.game.debug.list(cat);
    for (const p of providers) p.draw?.(this.graphics, view);
  }

  private notify(): void {
    const snap = this.enabled();
    for (const fn of this.listeners) fn(snap);
  }
}
